import { createModel } from '@rematch/core';
import { Paged } from 'domains/appDomain';
import {
  buildCreateOrgParam,
  buildUpdateOrgParam,
  buildUpdateOrgTypeParam,
  Company,
  Contact,
  CreateOrgParam,
  Organization,
  PartialOrg,
  UpdateOrgParam,
  UpdateOrgTypeParam,
} from 'domains/orgDomain';
import { PlatformRoleEnum } from 'domains/roleDomain';
import type { RootModel } from 'models';
import orgService from 'services/orgService';
import {
  buildCreateAddrParam,
  buildCreateCtParam,
  buildUpdateAddrParam,
  buildUpdateCtParam,
  CreateAddrParam,
  CreateCtParam,
  ListCtParam,
  PartialAddr,
  PartialCt,
  UpdateAddrParam,
  UpdateCtParam,
} from './../domains/orgDomain';
import { baseReducers, BaseState } from './baseReducers';

interface OrgState extends BaseState {
  list: Organization[];
  siderExpanded: boolean;
  curOrg?: PartialOrg;
  currentPage: number;
  pageSize: number;
  contacts?: Paged<Contact>;
  selectOrgUserContact?: Contact;
  curContact?: PartialCt;
  ctCurrentPage: number;
  ctPageSize: number;
  curAddr?: PartialAddr;
  companies: Company[];
  filterByCompanyId?: number;
  internalDistributionAddresses: string[];
}

export const org = createModel<RootModel>()({
  name: 'org',
  state: {
    list: [],
    siderExpanded: localStorage.getItem('orgSiderExpanded') === 'true',
    curOrg: undefined,
    contacts: undefined,
    selectOrgUserContact: undefined,
    curContact: undefined,
    ctCurrentPage: 1,
    ctPageSize: 20,
    curAddr: undefined,
    curAccount: undefined,
    currentPage: 1,
    pageSize: 20,
    companies: [],
    filterByCompanyId: undefined,
    internalDistributionAddresses: [],
  } as OrgState,
  reducers: {
    ...baseReducers<OrgState>(),
  },
  effects: (dispatch) => {
    const { org } = dispatch;
    return {
      async getOrgs(payload, state): Promise<void> {
        const resp = await orgService.getOrgs();
        org.setState({
          list: resp || [],
        });

        // REV-946 For Data Admin User, if we can't find org id in the authorized orgs
        // Then use the first org as the default org to display
        if (
          state.app.user?.platformRole ===
          PlatformRoleEnum.DataCustomerServiceAdmin
        ) {
          if (
            resp.length > 0 &&
            !resp.find((item) => item.id === state.app.orgId)
          ) {
            dispatch.app.setState({
              orgId: resp[0].id,
            });
            dispatch.app.switchOrg(resp[0].id);
          }
        }
      },

      async getInternalDistributionAddresses(orgId: number): Promise<void> {
        const resp = await orgService.getInternalDistributionAddresses(orgId);
        org.setState({
          internalDistributionAddresses: resp || [],
        });
      },

      async toggleSider(payload: boolean): Promise<void> {
        localStorage.setItem('orgSiderExpanded', String(payload));
        org.setState({
          siderExpanded: payload,
        });
      },

      async saveOrg(payload: PartialOrg): Promise<void> {
        if (payload.id) {
          console.log('payload', payload);
          // update existing
          const updateParam: UpdateOrgParam = buildUpdateOrgParam(payload);
          console.log('updateParam', updateParam);
          await orgService.updateOrg(updateParam);
        } else {
          // create new
          const createParam: CreateOrgParam = buildCreateOrgParam(payload);
          await orgService.createOrg(createParam);
        }
        await org.getOrgs();
      },

      async updateOrgType(payload: PartialOrg): Promise<void> {
        if (payload.id) {
          console.log('payload', payload);
          // update existing
          const updateOrgTypeParam: UpdateOrgTypeParam =
            buildUpdateOrgTypeParam(payload);
          console.log('updateParam', updateOrgTypeParam);
          await orgService.updateOrgType(updateOrgTypeParam);
        } else {
          console.error('Org ID is required to update org type');
        }
        await org.getOrgs();
      },

      async removeOrg(payload: number): Promise<void> {
        await orgService.removeOrg(payload);
        await org.getOrgs();
      },

      async getCompanies(): Promise<void> {
        const companies = await orgService.getCompanies();
        org.setState({ companies });
      },

      async saveCompany(payload: string): Promise<void> {
        await orgService.createCompany(payload);
        await org.getCompanies();
      },

      async getContacts(payload: ListCtParam): Promise<void> {
        let currentPage = payload.page.currentPage;
        let resp = await orgService.queryContacts(payload);
        if (resp.totalPages > 0 && currentPage > resp.totalPages) {
          // out of range
          currentPage = resp.totalPages;
          org.setState({
            contacts: resp,
            ctCurrentPage: currentPage,
          });
        } else {
          org.setState({
            contacts: resp,
          });
        }
      },

      async saveContact(payload: PartialCt, rootState): Promise<void> {
        if (payload.id) {
          // update existing
          const updateParam: UpdateCtParam = buildUpdateCtParam(payload);
          await orgService.updateContact(updateParam);
        } else {
          // create new
          const createParam: CreateCtParam = buildCreateCtParam(payload);
          await orgService.createContact(createParam);
        }
        await org.getContacts({
          orgId: payload.orgId || 0,
          page: {
            pageSize: rootState.org.ctPageSize,
            currentPage: rootState.org.ctCurrentPage,
          },
        });
      },

      async removeContact(payload: PartialCt, rootState): Promise<void> {
        if (!payload.id || !payload.orgId) return;
        await orgService.removeContact(payload);
        await org.getContacts({
          orgId: payload.orgId,
          page: {
            pageSize: rootState.org.ctPageSize,
            currentPage: rootState.org.ctCurrentPage,
          },
        });
      },

      async saveAddr(
        payload: { orgId: number; addr: PartialAddr },
        rootState
      ): Promise<void> {
        if (payload.addr.id) {
          // update existing
          const updateParam: UpdateAddrParam = buildUpdateAddrParam(
            payload.addr
          );
          await orgService.updateAddr(payload.orgId, updateParam);
        } else {
          // create new
          const createParam: CreateAddrParam = buildCreateAddrParam(
            payload.addr
          );
          await orgService.createAddr(payload.orgId, createParam);
        }
        await org.getContacts({
          orgId: payload.orgId || 0,
          page: {
            pageSize: rootState.org.ctPageSize,
            currentPage: rootState.org.ctCurrentPage,
          },
        });
      },

      async removeAddr(
        payload: { orgId: number; addr: PartialAddr },
        rootState
      ): Promise<void> {
        if (!payload.addr.id || !payload.orgId) return;
        await orgService.removeAddr(payload.addr.id);
        await org.getContacts({
          orgId: payload.orgId,
          page: {
            pageSize: rootState.org.ctPageSize,
            currentPage: rootState.org.ctCurrentPage,
          },
        });
      },

      async updateBoxChargeEditable(payload: {
        boxChargeEditable: boolean;
        orgId: number;
      }): Promise<void> {
        await orgService.updateBoxChargeEditable(
          Boolean(payload.boxChargeEditable),
          payload.orgId
        );
        await org.getOrgs();
      },

      async updateOrgShareContactDefault(payload: {
        shareContactOrgDefault: boolean;
        orgId: number;
      }): Promise<void> {
        await orgService.updateOrgShareContactDefault({
          shareContactOrgDefault: Boolean(payload.shareContactOrgDefault),
          orgId: payload.orgId,
        });
        await org.getOrgs();
      },
    };
  },
});
